export const COLLECTION_CONFIG_EXPORT_VERSION = 1;

export type CollectionConfigExportFile = {
  version: number;
  exportedAt: string;
  components: unknown[];
};

function normalizeSlug(slug: unknown): string {
  return String(slug ?? "")
    .trim()
    .toLowerCase()
    .replace(/[!]+$/, "");
}

function normalizeOrg(o: unknown): string {
  if (o == null || o === "") return "";
  return String(o).trim().toLowerCase();
}

export function findMatchingComponent(existing: unknown[], imported: Record<string, unknown>): unknown | undefined {
  const s = normalizeSlug(imported.slug);
  const org = normalizeOrg(imported.organization);
  return existing.find((c) => {
    const row = c as Record<string, unknown>;
    return normalizeSlug(row.slug) === s && normalizeOrg(row.organization) === org;
  });
}

/**
 * Normalized storage key for MongoDB collection name (same idea as backend processComponent).
 * Returns null if no explicit storage name is set (server may assign).
 */
export function getMongoCollectionKey(component: Record<string, unknown>): string | null {
  if (typeof component.mongoCollection === "string" && component.mongoCollection.trim()) {
    return component.mongoCollection.trim().toLowerCase();
  }
  const cols = component.collections;
  if (cols && typeof cols === "object" && cols !== null && !Array.isArray(cols)) {
    const mongo = (cols as Record<string, unknown>).mongo;
    if (typeof mongo === "string" && mongo.trim()) {
      return mongo.trim().toLowerCase();
    }
    const legacy = (cols as Record<string, unknown>).legacy;
    if (typeof legacy === "string" && legacy.trim()) {
      return legacy.trim().toLowerCase();
    }
  }
  if (typeof component.collection === "string" && component.collection.trim()) {
    return component.collection.trim().toLowerCase();
  }
  return null;
}

/** Another component (different uid) already uses this mongo collection name. */
export function findOtherComponentWithMongoCollection(
  existing: unknown[],
  mongoKey: string,
  excludeUid?: string | null
): unknown | undefined {
  const ex = excludeUid != null && String(excludeUid).trim() !== "" ? String(excludeUid).trim() : null;
  return existing.find((c) => {
    const row = c as Record<string, unknown>;
    const uid = row.uid != null ? String(row.uid).trim() : "";
    if (ex && uid === ex) return false;
    const key = getMongoCollectionKey(row);
    return key !== null && key === mongoKey;
  });
}

/** Stable key for slug + organization scope (matches duplicate checks). */
export function getSlugOrganizationKey(component: Record<string, unknown>): string {
  return `${normalizeSlug(component.slug)}\u0000${normalizeOrg(component.organization)}`;
}

/** Another component (different uid) already uses this slug + organization pair. */
export function findOtherComponentWithSlugOrganization(
  existing: unknown[],
  imported: Record<string, unknown>,
  excludeUid?: string | null
): unknown | undefined {
  if (!normalizeSlug(imported.slug)) return undefined;
  const want = getSlugOrganizationKey(imported);
  const ex = excludeUid != null && String(excludeUid).trim() !== "" ? String(excludeUid).trim() : null;
  return existing.find((c) => {
    const row = c as Record<string, unknown>;
    const uid = row.uid != null ? String(row.uid).trim() : "";
    if (ex && uid === ex) return false;
    return getSlugOrganizationKey(row) === want;
  });
}

export function stripComponentForCreate(component: Record<string, unknown>): Record<string, unknown> {
  const omit = new Set([
    "uid",
    "_id",
    "__v",
    "created",
    "updated",
    "createdBy",
    "_createdBy",
    "creatorEmail",
    "creatorDisplayName",
    "creatorIsAdmin"
  ]);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(component)) {
    if (omit.has(k)) continue;
    out[k] = v;
  }
  return out;
}

export function mergeImportedForUpdate(
  imported: Record<string, unknown>,
  existing: Record<string, unknown>
): Record<string, unknown> {
  const base = stripComponentForCreate(imported);
  base.uid = existing.uid;
  if (existing.createdBy != null) base.createdBy = existing.createdBy;
  if (existing._createdBy != null) base._createdBy = existing._createdBy;
  return base;
}

export function parseCollectionConfigImportData(data: unknown): unknown[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  if (o.labwickCollectionExport && typeof o.labwickCollectionExport === "object") {
    return parseCollectionConfigImportData(o.labwickCollectionExport);
  }
  if (Array.isArray(o.components)) return o.components;
  if (o.component && typeof o.component === "object") return [o.component];
  if (typeof o.slug === "string" && o.fields !== undefined) return [o];
  return [];
}

export function buildCollectionExportBlob(components: unknown[]): Blob {
  const payload: CollectionConfigExportFile = {
    version: COLLECTION_CONFIG_EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    components: components.map((c) => JSON.parse(JSON.stringify(c)))
  };
  return new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
